import { memo, useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { cn } from '../../lib/cn';
import { isMultiCellPaste, type GridPosition } from './tracker-state';

export interface GridCellHandlers {
  onMouseDown: (kpiId: string, weekId: string, extend: boolean) => void;
  onMouseEnter: (kpiId: string, weekId: string) => void;
  onDoubleClick: (kpiId: string, weekId: string) => void;
  onOpenComment: (kpiId: string, weekId: string) => void;
  onCommit: (raw: string, move?: GridPosition) => void;
  onCancel: () => void;
  /** The editor's text on every keystroke, so dependent calculated cells recalculate before the commit. */
  onDraft: (kpiId: string, weekId: string, raw: string) => void;
  onPasteBlock: (text: string, kpiId: string, weekId: string) => void;
}

export interface GridCellProps {
  kpiId: string;
  weekId: string;
  text: string;
  /** Neutral movement glyph and its tooltip; absent when there is nothing to compare. */
  glyph: string | null;
  glyphTitle: string | null;
  title: string;
  isBlank: boolean;
  editable: boolean;
  /** A calculated KPI's cell: read-only, marked `ƒ`, on a faint lavender wash. */
  calculated: boolean;
  selected: boolean;
  focused: boolean;
  edited: boolean;
  invalid: boolean;
  hasComment: boolean;
  isCurrentWeek: boolean;
  /** A parent KPI's cell whose children add up to something else that week: the soft marker's tooltip, else `null`. */
  childrenHint: string | null;
  /** The editor's starting text while this cell is being edited, otherwise `null`. */
  editorValue: string | null;
  handlers: GridCellHandlers;
}

/**
 * The text input of the cell being edited. It owns its draft, so typing re-renders one input, not the grid, and it
 * commits exactly once: Enter (then down), Tab (then right; Shift+Tab left), or leaving the cell.
 */
const CellEditor = ({ initial, kpiId, weekId, handlers }: { initial: string; kpiId: string; weekId: string; handlers: GridCellHandlers }) => {
  const [draft, setDraft] = useState(initial);
  const done = useRef(false);
  const input = useRef<HTMLInputElement>(null);

  // The starting text (a typed first character included) is the first draft.
  useEffect(() => {
    handlers.onDraft(kpiId, weekId, initial);
  }, [handlers, kpiId, weekId, initial]);

  useEffect(() => {
    const element = input.current;

    if (element) {
      element.focus();
      // A typed first character replaces the value and keeps the caret after it; otherwise select it all.
      element.setSelectionRange(initial.length, initial.length);
    }
  }, [initial]);

  const finish = (action: () => void): void => {
    if (!done.current) {
      done.current = true;
      action();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      finish(() => handlers.onCommit(draft, { row: event.shiftKey ? -1 : 1, col: 0 }));
    } else if (event.key === 'Tab') {
      event.preventDefault();
      finish(() => handlers.onCommit(draft, { row: 0, col: event.shiftKey ? -1 : 1 }));
    } else if (event.key === 'Escape') {
      event.preventDefault();
      finish(handlers.onCancel);
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    const text = event.clipboardData.getData('text/plain');

    if (isMultiCellPaste(text)) {
      event.preventDefault();
      finish(() => handlers.onPasteBlock(text, kpiId, weekId));
    }
  };

  return (
    <input
      ref={input}
      value={draft}
      aria-label="Cell value"
      onChange={(event) => {
        setDraft(event.target.value);
        handlers.onDraft(kpiId, weekId, event.target.value);
      }}
      onKeyDown={onKeyDown}
      onBlur={() => finish(() => handlers.onCommit(draft))}
      onPaste={onPaste}
      className="absolute inset-0 h-full w-full bg-background px-2 text-end font-mono text-xs text-foreground outline outline-2 -outline-offset-2 outline-ring"
    />
  );
};

/**
 * One weekly cell. A read-only cell (a VIEW pillar, a calculated KPI, a MANAGER) can be selected and totalled but
 * carries no editing affordance at all: no cell cursor, no double-click, no editor. A calculated cell shows an
 * italic `ƒ` and a lavender wash, nothing louder (plan §2.2).
 */
export const GridCell = memo((props: GridCellProps) => {
  const { kpiId, weekId, handlers } = props;

  const onMouseDown = (event: MouseEvent): void => {
    if (event.button === 0) {
      event.preventDefault();
      handlers.onMouseDown(kpiId, weekId, event.shiftKey);
    }
  };

  return (
    <td
      role="gridcell"
      data-kpi-id={kpiId}
      data-week-id={weekId}
      aria-selected={props.selected}
      aria-readonly={!props.editable}
      title={props.childrenHint ? `${props.title}\n${props.childrenHint}` : props.title}
      onMouseDown={onMouseDown}
      onMouseEnter={() => handlers.onMouseEnter(kpiId, weekId)}
      onDoubleClick={props.editable ? () => handlers.onDoubleClick(kpiId, weekId) : undefined}
      className={cn(
        'relative h-8 min-w-[7.5rem] select-none border-b border-e border-border/60 px-2 text-end align-middle',
        props.editable ? 'cursor-cell' : 'cursor-default',
        props.isCurrentWeek && 'bg-primary/[0.04]',
        props.calculated && 'bg-violet-50/70 dark:bg-violet-950/20',
        props.edited && 'bg-primary/[0.08]',
        props.selected && 'bg-sky-100/70 dark:bg-sky-900/30',
        props.invalid && 'bg-destructive/10',
        props.focused && 'outline outline-2 -outline-offset-2 outline-ring',
      )}
    >
      {props.editorValue !== null ? (
        <CellEditor
          initial={props.editorValue}
          kpiId={kpiId}
          weekId={weekId}
          handlers={handlers}
        />
      ) : (
        <>
          {props.hasComment ? (
            <button
              type="button"
              aria-label="Open comment"
              data-comment-marker
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => handlers.onOpenComment(kpiId, weekId)}
              className="absolute start-0 top-0 size-0 border-e-[7px] border-t-[7px] border-e-transparent border-t-amber-600"
            />
          ) : null}
          {props.edited ? (
            <span
              aria-label="Unsaved change"
              className="absolute end-0.5 top-0.5 size-1.5 rounded-full bg-primary"
            />
          ) : null}
          {props.childrenHint ? (
            <span
              aria-label={props.childrenHint}
              title={props.childrenHint}
              data-children-hint
              className="absolute bottom-0.5 start-1 font-mono text-[9px] leading-none text-muted-foreground"
            >
              Σ≠
            </span>
          ) : null}
          {props.calculated ? (
            <span
              aria-label="Calculated"
              data-calculated
              className="absolute start-1 top-0.5 font-serif text-[10px] italic text-violet-700/70 dark:text-violet-300/70"
            >
              ƒ
            </span>
          ) : null}
          <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap">
            {props.glyph ? (
              <span
                title={props.glyphTitle ?? undefined}
                data-movement
                className="font-mono text-[10px] text-muted-foreground"
              >
                {props.glyph}
              </span>
            ) : null}
            <span
              data-cell-text
              className={cn(
                'font-mono text-xs',
                props.isBlank ? 'text-muted-foreground/60' : 'font-medium text-foreground',
                props.invalid && 'text-destructive',
              )}
            >
              {props.text}
            </span>
          </span>
        </>
      )}
    </td>
  );
});

GridCell.displayName = 'GridCell';
